import React from 'react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';
import { useUserPhotoBlobUrl } from '../../modules/users/useData';

export type UserAvatarSize = 'xs' | 'sm' | 'md' | 'lg';

const SIZE_CLASSES: Record<UserAvatarSize, string> = {
  xs: 'h-7 w-7 text-[10px]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
};

export function getUserDisplayName(user: {
  userDisplayName?: string | null;
  userFirstName?: string | null;
  userLastName?: string | null;
  userEmail?: string | null;
}): string {
  const display = user.userDisplayName?.trim();
  if (display) return display;
  const fullName = [user.userFirstName, user.userLastName].filter(Boolean).join(' ').trim();
  if (fullName) return fullName;
  return user.userEmail?.trim() || 'User';
}

export function getUserInitials(user: {
  userFirstName?: string | null;
  userLastName?: string | null;
  userEmail?: string | null;
  userDisplayName?: string | null;
}): string {
  const fromName = `${user.userFirstName?.[0] ?? ''}${user.userLastName?.[0] ?? ''}`.trim();
  if (fromName) return fromName.toUpperCase();
  const display = user.userDisplayName?.trim();
  if (display) return display.charAt(0).toUpperCase();
  return (user.userEmail?.[0] ?? 'U').toUpperCase();
}

export interface UserAvatarProps {
  userId?: number;
  photoPath?: string | null;
  userFirstName?: string | null;
  userLastName?: string | null;
  userEmail?: string | null;
  userDisplayName?: string | null;
  size?: UserAvatarSize;
  className?: string;
  rounded?: 'full' | 'xl';
}

const UserAvatar: React.FC<UserAvatarProps> = ({
  userId,
  photoPath,
  userFirstName,
  userLastName,
  userEmail,
  userDisplayName,
  size = 'sm',
  className,
  rounded = 'full',
}) => {
  const { colors } = useTheme();
  const { photoBlobUrl, loading } = useUserPhotoBlobUrl(userId, photoPath);
  const initials = getUserInitials({ userFirstName, userLastName, userEmail, userDisplayName });
  const roundedClass = rounded === 'full' ? 'rounded-full' : 'rounded-xl';

  return (
    <div
      className={cn(
        'shrink-0 overflow-hidden flex items-center justify-center font-semibold',
        SIZE_CLASSES[size],
        roundedClass,
        !photoBlobUrl && cn(colors.primary, colors.primaryText),
        className,
      )}
      aria-hidden={!photoBlobUrl}
    >
      {loading ? (
        <span className={cn('font-normal', colors.textMuted)}>…</span>
      ) : photoBlobUrl ? (
        <img src={photoBlobUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        initials
      )}
    </div>
  );
};

export default UserAvatar;
